import type { ApiClient } from "../api/client";
import { isApiError } from "../api/errors";
import type { AppEvent, EventResource, MixOutputTrack, Playback, QueueSnapshot } from "../api/types";
import { createStore } from "./store";

/**
 * 再生状態とキューの同期（仕様書「同時操作と状態同期」）。
 *
 * - 正本は /playback と /queue のスナップショット。SSE は「変わった」という通知にだけ使う
 * - 先に SSE を開き、開けてからスナップショットを取る（開くまでの間の変化を取りこぼさない）
 * - SSE が切れた・定期の取り直しに失敗した・端末がオフラインになった → 「更新停止中」にして、再生位置の補間を止める
 * - つなぎ直したら、必ず再生状態とキューを取り直してから「最新」に戻す
 */

export type Connection = "idle" | "connecting" | "live" | "stale" | "reconnecting";

export interface SyncState {
  conn: Connection;
  playback: Playback | null;
  queue: QueueSnapshot | null;
  /** サーバーの時計 − 端末の時計（ミリ秒）。往復の真ん中で受け取ったとみなす */
  clockOffsetMs: number;
  /** 最後に最新だと確かめた時刻（端末の時計） */
  lastSyncAt: number | null;
  /** 更新が止まった時刻 */
  staleSince: number | null;
  /** 更新停止中に止めておく位置（止まった時点の位置） */
  frozenPositionMs: number | null;
  /** 最後の取得の失敗 */
  errorCode: string | null;
  /** つなぎ直しの回数（0 は最初の接続） */
  attempt: number;
}

export const initialSyncState: SyncState = {
  conn: "idle",
  playback: null,
  queue: null,
  clockOffsetMs: 0,
  lastSyncAt: null,
  staleSince: null,
  frozenPositionMs: null,
  errorCode: null,
  attempt: 0,
};

/** 区間の長さ */
function currentLength(p: Playback | null): number | null {
  const seg = p?.current?.performance.segment;
  return seg ? Math.max(0, seg.end_ms - seg.start_ms) : null;
}

/** Source milliseconds per output millisecond, verified by the server's PCM manifest. */
export function mixSourceRate(track: MixOutputTrack): number | null {
  if (track.mapping_accuracy === "exact") return 1;
  const rate = track.source_rate;
  return track.mapping_accuracy === "duration_estimate" && typeof rate === "number" &&
    Number.isFinite(rate) && rate > 0 ? rate : null;
}

/**
 * 画面に出す再生位置（区間の開始からのミリ秒）。
 * 最新とつながっていて、再生中のときだけ、サーバーの時刻から進める。更新停止中は止めた位置のまま。
 */
export function positionAt(
  s: Pick<SyncState, "conn" | "playback" | "clockOffsetMs" | "frozenPositionMs"> & Partial<Pick<SyncState, "staleSince">>,
  now: number,
  mixTrack?: MixOutputTrack,
): number | null {
  const p = s.playback;
  if (mixTrack) {
    const mix = p?.mix;
    const output = mix?.output;
    if (!mix || !["active", "draining"].includes(mix.phase) || output?.status !== "confirmed" ||
        !output.tracks.some((track) => track.plan_id === mixTrack.plan_id && track.occurrence_id === mixTrack.occurrence_id)) return null;
    const length = Math.max(0, mixTrack.source_end_ms - mixTrack.source_start_ms);
    const base = mixTrack.source_position_ms - mixTrack.source_start_ms;
    const clamp = (value: number) => Math.max(0, Math.min(length, value));
    // Only an exact map or a rate verified from the measured transform can tick.
    // Other estimates retain their last confirmed source position.
    const rate = mixSourceRate(mixTrack);
    const anchor = Date.parse(output.updated_at);
    if (mix.pause_reasons.length || rate === null || !Number.isFinite(anchor)) return clamp(base);
    const stoppedAt = s.conn === "live" ? now : s.staleSince;
    if (stoppedAt == null) return clamp(base);
    return clamp(base + Math.max(0, stoppedAt + s.clockOffsetMs - anchor) * rate);
  }
  const length = currentLength(p);
  if (!p || length === null) return null;
  const clamp = (v: number) => Math.max(0, Math.min(length, v));
  if (s.conn !== "live") return clamp(s.frozenPositionMs ?? p.position_ms);
  if (p.state !== "playing") return clamp(p.position_ms);
  const measuredAt = Date.parse(p.updated_at);
  if (Number.isNaN(measuredAt)) return clamp(p.position_ms);
  const serverNow = now + s.clockOffsetMs;
  return clamp(p.position_ms + Math.max(0, serverNow - measuredAt));
}

export interface SyncOptions {
  /** 最新のあいだ、念のため取り直す間隔（定期的な実状態での補正） */
  pollMs?: number;
  /** つなぎ直すまでの待ち（回数ごと） */
  backoffMs?: number[];
  now?: () => number;
}

type Listener = (event: AppEvent) => void;

export class PlaybackSync {
  readonly store = createStore<SyncState>(initialSyncState);
  private unsubscribe: (() => void) | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private running = false;
  private generationOfLoad = 0;
  private eventListeners = new Set<Listener>();
  private connectedListeners = new Set<() => void>();
  private readonly pollMs: number;
  private readonly backoffMs: number[];
  private readonly now: () => number;

  constructor(
    private api: ApiClient,
    opts: SyncOptions = {},
  ) {
    this.pollMs = opts.pollMs ?? 20_000;
    this.backoffMs = opts.backoffMs ?? [1000, 2000, 4000, 8000, 15000, 30000];
    this.now = opts.now ?? Date.now;
  }

  /** SSE の通知を受け取る（ここ好き・投稿・ライブラリの更新に使う） */
  onEvent(fn: Listener): () => void {
    this.eventListeners.add(fn);
    return () => this.eventListeners.delete(fn);
  }

  /** つながって（つなぎ直して）、スナップショットを取り直したとき */
  onConnected(fn: () => void): () => void {
    this.connectedListeners.add(fn);
    return () => this.connectedListeners.delete(fn);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.store.set((s) => ({ ...s, conn: "connecting", attempt: 0 }));
    this.connect();
    if (typeof window !== "undefined") {
      window.addEventListener("offline", this.handleOffline);
      window.addEventListener("online", this.handleOnline);
      document.addEventListener("visibilitychange", this.handleVisible);
    }
  }

  stop() {
    this.running = false;
    this.unsubscribe?.();
    this.unsubscribe = null;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.stopPolling();
    if (typeof window !== "undefined") {
      window.removeEventListener("offline", this.handleOffline);
      window.removeEventListener("online", this.handleOnline);
      document.removeEventListener("visibilitychange", this.handleVisible);
    }
    this.store.set(initialSyncState);
  }

  /** 操作のあとなど、いますぐ取り直す */
  async refresh(resources: Array<"playback" | "queue"> = ["playback", "queue"]): Promise<void> {
    if (!this.running) return;
    try {
      await this.load(resources);
    } catch (e) {
      this.markStale(isApiError(e) ? e.code : "network");
      this.scheduleReconnect();
    }
  }

  /** 手動でつなぎ直す（「いますぐ再接続」） */
  reconnectNow() {
    if (!this.running) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.store.set((s) => ({ ...s, conn: s.conn === "live" ? "live" : "reconnecting" }));
    this.connect();
  }

  private connect() {
    const myLoad = ++this.generationOfLoad;
    this.unsubscribe = this.api.subscribe({
      onOpen: () => {
        if (myLoad !== this.generationOfLoad) return;
        // つないだ直後は必ずスナップショットを取り直す
        this.load(["playback", "queue"])
          .then(() => {
            if (myLoad !== this.generationOfLoad) return;
            const wasReconnect = this.store.get().attempt > 0 || this.store.get().staleSince !== null;
            this.store.set((s) => ({ ...s, conn: "live", staleSince: null, frozenPositionMs: null, errorCode: null, attempt: 0 }));
            this.startPolling();
            if (wasReconnect) this.connectedListeners.forEach((fn) => fn());
          })
          .catch((e) => {
            if (myLoad !== this.generationOfLoad) return;
            this.unsubscribe?.();
            this.unsubscribe = null;
            this.markStale(isApiError(e) ? e.code : "network");
            this.scheduleReconnect();
          });
      },
      onEvent: (event) => {
        if (myLoad !== this.generationOfLoad) return;
        this.handleEvent(event);
      },
      onError: () => {
        if (myLoad !== this.generationOfLoad) return;
        this.unsubscribe = null;
        this.markStale("network");
        this.scheduleReconnect();
      },
    });
  }

  private handleEvent(event: AppEvent) {
    const wanted = event.resources.filter((r): r is "playback" | "queue" => r === "playback" || r === "queue");
    if (event.type === "access_revoked") {
      wanted.push("playback", "queue");
    }
    if (wanted.length > 0 && this.store.get().conn === "live") {
      void this.refresh([...new Set(wanted)]);
    }
    this.eventListeners.forEach((fn) => fn(event));
  }

  private async load(resources: Array<"playback" | "queue">) {
    const t0 = this.now();
    const [playback, queue] = await Promise.all([
      resources.includes("playback") ? this.api.getPlayback() : Promise.resolve(null),
      resources.includes("queue") ? this.api.getQueue() : Promise.resolve(null),
    ]);
    const t1 = this.now();
    this.store.set((s) => {
      const next: SyncState = { ...s, lastSyncAt: t1, errorCode: null };
      if (playback) {
        const serverTime = Date.parse(playback.server_time);
        next.playback = playback;
        if (!Number.isNaN(serverTime)) next.clockOffsetMs = serverTime - (t0 + t1) / 2;
      }
      if (queue) next.queue = queue;
      // 古い応答で新しいキューを上書きしない
      if (queue && s.queue && queue.session_id === s.queue.session_id && queue.revision < s.queue.revision) next.queue = s.queue;
      return next;
    });
  }

  private markStale(code: string) {
    const now = this.now();
    this.stopPolling();
    this.store.set((s) => {
      if (s.conn === "stale" || s.conn === "reconnecting") return { ...s, errorCode: code, conn: "stale" };
      return {
        ...s,
        conn: "stale",
        staleSince: now,
        // 止まった時点の位置で止める（以後、見かけだけ進めない）
        frozenPositionMs: s.conn === "live" ? positionAt(s, now) : (s.frozenPositionMs ?? s.playback?.position_ms ?? null),
        errorCode: code,
      };
    });
  }

  private scheduleReconnect() {
    if (!this.running || this.reconnectTimer) return;
    const attempt = this.store.get().attempt;
    const delay = this.backoffMs[Math.min(attempt, this.backoffMs.length - 1)];
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.running) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        this.store.set((s) => ({ ...s, attempt: s.attempt + 1 }));
        this.scheduleReconnect();
        return;
      }
      this.store.set((s) => ({ ...s, conn: "reconnecting", attempt: s.attempt + 1 }));
      this.connect();
    }, delay);
  }

  private startPolling() {
    this.stopPolling();
    this.pollTimer = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (this.store.get().conn !== "live") return;
      void this.refresh(["playback", "queue"]);
    }, this.pollMs);
  }

  private stopPolling() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  private handleOffline = () => {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.generationOfLoad++;
    this.markStale("network");
    this.scheduleReconnect();
  };

  private handleOnline = () => {
    if (this.store.get().conn !== "live") this.reconnectNow();
  };

  private handleVisible = () => {
    if (document.visibilityState === "visible" && this.store.get().conn === "live") void this.refresh();
  };
}

export function isEventFor(event: AppEvent, resource: EventResource): boolean {
  return event.type === "access_revoked" || event.resources.includes(resource);
}
